
import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TeamChatMessage, TeamMember } from "@/integrations/supabase/types/";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Paperclip, Mic, X } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import { FileAttachment } from "@/components/chat/FileAttachment";
import { AttachmentDisplay } from "@/components/chat/AttachmentDisplay";
import { AudioRecorder } from "@/components/chat/AudioRecorder";

interface TeamChatMessagesProps {
  selectedMember: TeamMember | null;
}

export const TeamChatMessages = ({ selectedMember }: TeamChatMessagesProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: currentUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: messages, isLoading } = useQuery({
    queryKey: ["team-chat-messages", selectedMember?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      let query = supabase
        .from("team_chat_messages")
        .select(`
          *,
          sender:profiles!sender_id(first_name, last_name, email)
        `)
        .order("created_at", { ascending: true });

      if (selectedMember) {
        // Private messages between current user and selected member
        query = query.eq("is_private", true).or(
          `and(sender_id.eq.${user.id},recipient_id.eq.${selectedMember.user_id}),and(sender_id.eq.${selectedMember.user_id},recipient_id.eq.${user.id})`
        );
      } else {
        // Public general chat
        query = query.eq("channel", "general").eq("is_private", false);
      }

      const { data: messagesData, error } = await query;

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load messages",
          variant: "destructive",
        });
        throw error;
      }

      // Fetch attachments separately
      if (!messagesData || messagesData.length === 0) return messagesData;

      const messageIds = messagesData.map(m => m.id);
      const { data: attachments } = await supabase
        .from("chat_attachments")
        .select("*")
        .in("message_id", messageIds);

      // Attach attachments to messages
      const messagesWithAttachments = messagesData.map(msg => ({
        ...msg,
        chat_attachments: attachments?.filter(att => att.message_id === msg.id) || []
      }));

      return messagesWithAttachments;
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({ content, files }: { content: string; files?: File[] }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const messageData = {
        sender_id: user.id,
        content,
        channel: selectedMember ? "private" : "general",
        is_private: !!selectedMember,
        recipient_id: selectedMember?.user_id || null,
      };

      const { data: newMessage, error } = await supabase
        .from("team_chat_messages")
        .insert([messageData])
        .select()
        .single();

      if (error) throw error;

      // Upload attachments if any
      if (files && files.length > 0) {
        for (const file of files) {
          const filePath = `${user.id}/${Date.now()}-${file.name}`;
          
          const { error: uploadError } = await supabase.storage
            .from('chat-attachments')
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          const { error: attachmentError } = await supabase
            .from('chat_attachments')
            .insert({
              message_id: newMessage.id,
              file_name: file.name,
              file_path: filePath,
              file_type: file.type.split('/')[0],
              file_size: file.size,
              mime_type: file.type,
              uploaded_by: user.id,
            });

          if (attachmentError) throw attachmentError;
        }
      }
    },
    onSuccess: () => {
      setMessage("");
      setAttachedFiles([]);
      queryClient.invalidateQueries({
        queryKey: ["team-chat-messages", selectedMember?.id],
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel("team-chat")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "team_chat_messages",
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["team-chat-messages", selectedMember?.id],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, selectedMember?.id]);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (!message.trim() && attachedFiles.length === 0) return;
    sendMessageMutation.mutate({ 
      content: message || "Sent an attachment", 
      files: attachedFiles 
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 20MB limit`,
          variant: "destructive",
        });
        return false;
      }
      return true;
    });
    
    setAttachedFiles(prev => [...prev, ...validFiles].slice(0, 10));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAudioRecording = async (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    
    await sendMessageMutation.mutateAsync({ 
      content: "Sent a voice message", 
      files: [audioFile] 
    });
    
    setIsRecording(false);
  };

  return (
    <div className="flex-1 flex flex-col p-4">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">
          {selectedMember
            ? `Chat with ${selectedMember.first_name} ${selectedMember.last_name}`
            : "General Chat"}
        </h2>
      </div>

      <ScrollArea ref={scrollAreaRef} className="flex-1 pr-4">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start space-x-4">
                <div className="h-8 w-8 rounded-full bg-muted" />
                <div className="space-y-2">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-12 w-64 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {messages?.map((msg: any) => {
              const isOwnMessage = msg.sender_id === currentUser?.id;
              const senderName = msg.sender 
                ? `${msg.sender.first_name} ${msg.sender.last_name}`.trim() || msg.sender.email
                : 'Unknown';
              const initials = msg.sender?.first_name?.[0] + (msg.sender?.last_name?.[0] || '');

              return (
                <div 
                  key={msg.id} 
                  className={`flex items-start space-x-4 ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}
                >
                  <Avatar>
                    <AvatarFallback className="text-xs">
                      {initials || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 max-w-[70%]">
                    {!isOwnMessage && (
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        {senderName}
                      </p>
                    )}
                    <div className={`rounded-lg p-3 ${
                      isOwnMessage 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted'
                    }`}>
                      <p className="text-sm break-words">{msg.content}</p>
                      
                      {msg.chat_attachments && msg.chat_attachments.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {msg.chat_attachments.map((attachment: any) => (
                            <AttachmentDisplay key={attachment.id} attachment={attachment} />
                          ))}
                        </div>
                      )}
                      
                      <span className={`text-xs mt-1 block ${
                        isOwnMessage 
                          ? 'text-primary-foreground/70' 
                          : 'text-muted-foreground'
                      }`}>
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {isRecording ? (
        <AudioRecorder
          onRecordingComplete={handleAudioRecording}
          onCancel={() => setIsRecording(false)}
        />
      ) : (
        <div className="mt-4 space-y-2">
          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachedFiles.map((file, index) => (
                <FileAttachment
                  key={index}
                  file={file}
                  onRemove={() => removeFile(index)}
                  showRemove={true}
                />
              ))}
            </div>
          )}
          
          <div className="flex space-x-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            />
            
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={sendMessageMutation.isPending}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setIsRecording(true)}
              disabled={sendMessageMutation.isPending}
            >
              <Mic className="h-4 w-4" />
            </Button>
            
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Message ${
                selectedMember
                  ? `${selectedMember.first_name} ${selectedMember.last_name}`
                  : "general chat"
              }...`}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              disabled={sendMessageMutation.isPending}
            />
            
            <Button
              onClick={handleSendMessage}
              disabled={sendMessageMutation.isPending || (!message.trim() && attachedFiles.length === 0)}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
